import React, { useState } from "react";
import { orbStyle, orbImage, orbImageStyle } from "./supernovaOrbs";
import { SUPERNOVA_TOKENS } from "./supernovaContracts";

/**
 * SupernovaOrb — one token's artwork, everywhere the tab shows a token.
 *
 * Renders the official collection image as a gold-ringed disc. If that image
 * fails to load — bad cache, blocked request, a build that dropped the asset —
 * it falls back to the CSS orb rather than a broken-image icon. The fallback is
 * the same size, same circle and same ring, so a failure changes the fidelity
 * of one bullet and nothing about the layout around it.
 *
 * `name` is the token's display name and becomes the alt text, with three cases:
 *   name="Celestial Flare"  the caller's own label — used as-is
 *   name={null}             decorative: the caller already names the token in
 *                           text beside the orb, so alt text would make a
 *                           screen reader say it twice
 *   name omitted            we fall back to the canonical name for the id, and
 *                           if that doesn't resolve either, to decorative —
 *                           better silent than an invented label
 *
 * `shape` is how the art is framed:
 *   "disc"   the gold-ringed `size`px circle — lists, table rows, the feed
 *   "cover"  fills whatever box the caller gives it, no ring and no size of its
 *            own — the collection card's full-bleed artwork. The caller owns
 *            the frame; `size` is ignored.
 */

const canonicalName = (tokenId) => {
  const match = SUPERNOVA_TOKENS.find((t) => t.id === tokenId);
  return match ? match.name : null;
};

const SupernovaOrb = ({ tokenId, size = 42, name, className, shape = "disc" }) => {
  /* Remember WHICH src broke, not just that one did: the stake modal swaps
     tokenId under the same element, and a boolean would leave a healthy image
     hidden after an unrelated sibling failed. */
  const [brokenSrc, setBrokenSrc] = useState(null);

  const src = orbImage(tokenId);
  /* An explicit null is the caller saying "decorative" — distinct from an
     omitted name, which means "I haven't got one, use yours". */
  const label = name === null ? null : name || canonicalName(tokenId);
  const cover = shape === "cover";

  if (!src || brokenSrc === src) {
    /* Cover keeps the token's gradient but drops the disc: the caller's frame
       is the shape, so a circle inside it would read as a failed crop rather
       than as the fallback it is. */
    const style = cover
      ? { display: "block", background: orbStyle(tokenId, size).background }
      : orbStyle(tokenId, size);
    return (
      <span
        style={style}
        className={className}
        aria-hidden="true"
        data-testid="supernova-orb-fallback"
      />
    );
  }

  return (
    <img
      src={src}
      alt={label || ""}
      width={cover ? undefined : size}
      height={cover ? undefined : size}
      loading="lazy"
      decoding="async"
      draggable="false"
      onError={() => setBrokenSrc(src)}
      style={cover ? undefined : orbImageStyle(size)}
      className={className}
      data-testid="supernova-orb"
    />
  );
};

export default SupernovaOrb;
